// src/utils/reporteProductosGenerator.js

import { obtenerDatosEmpresa } from './tenantConfig';
import html2pdf from 'html2pdf.js';

// Configuración estricta de JEAN INFORMATICO C.A.
export const JEAN_INFORMATICO_CONFIG = {
  nombre: "JEAN INFORMATICO C.A.",
  rif: "J-41023119-0",
  direccion: "Calle 30 con Av. 33, C.C. Latin Center P/B, Local 01 - Acarigua, Portuguesa",
  telefono: "0412-5696219 / 0416-7594723",
  whatsapp: "04167594723",
  ciudad: "Acarigua, Estado Portuguesa",
  email: "soportejeaninformatico@gmail.com",
  sitioWeb: "www.jeaninformatico.com.ve",
  slogan: "Servicio Técnico Especializado",
  lema: "Tecnología al servicio de la gente"
};

// ==================== CONFIG EMPRESA ====================

const getEmpresa = () => {
  try {
    const datos = obtenerDatosEmpresa() || {};
    return {
      // Forzamos el nombre correcto si los datos del tenant vienen vacíos o con placeholders
      nombre: (datos.nombreComercial && datos.nombreComercial !== 'MI EMPRESA') 
              ? datos.nombreComercial 
              : JEAN_INFORMATICO_CONFIG.nombre,
      rif: datos.rif || JEAN_INFORMATICO_CONFIG.rif,
      direccion: datos.direccion || JEAN_INFORMATICO_CONFIG.direccion,
      telefono: datos.telefono1 || JEAN_INFORMATICO_CONFIG.telefono,
      whatsapp: datos.whatsapp || JEAN_INFORMATICO_CONFIG.whatsapp,
      ciudad: datos.ciudad || JEAN_INFORMATICO_CONFIG.ciudad,
      email: datos.email || JEAN_INFORMATICO_CONFIG.email,
      sitioWeb: datos.sitioWeb || JEAN_INFORMATICO_CONFIG.sitioWeb,
      slogan: datos.slogan || JEAN_INFORMATICO_CONFIG.slogan,
      lema: datos.slogan || JEAN_INFORMATICO_CONFIG.lema,
      logoBase64: datos.logoBase64 || null,
    };
  } catch (err) {
    return { ...JEAN_INFORMATICO_CONFIG, logoBase64: null };
  }
};

// ==================== LOGO ====================

let LOGO_BASE64 = '';
let LOGO_MIME = 'image/jpeg';

const detectarMimeDesdeBase64 = (b64) => {
  if (!b64) return 'image/jpeg';
  if (b64.startsWith('/9j/')) return 'image/jpeg';
  if (b64.startsWith('iVBORw0KGgo')) return 'image/png';
  if (b64.startsWith('R0lGOD')) return 'image/gif';
  if (b64.startsWith('UklGR')) return 'image/webp';
  return 'image/jpeg';
};

const limpiarBase64 = (raw) => {
  if (!raw) return '';
  if (raw.includes(',')) return raw.split(',')[1];
  return raw;
};

export const cargarLogo = async () => {
  try {
    const empresa = getEmpresa();
    if (empresa.logoBase64) {
      const limpio = limpiarBase64(empresa.logoBase64);
      if (limpio && limpio.length > 50) {
        LOGO_BASE64 = limpio;
        LOGO_MIME = detectarMimeDesdeBase64(limpio);
        return LOGO_BASE64;
      }
    }
    const response = await fetch('/logo-jean.jpg');
    if (!response.ok) { LOGO_BASE64 = ''; return ''; }
    const blob = await response.blob();
    LOGO_MIME = blob.type || 'image/jpeg';

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        LOGO_BASE64 = limpiarBase64(reader.result);
        resolve(LOGO_BASE64);
      };
      reader.onerror = () => { LOGO_BASE64 = ''; resolve(''); };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    LOGO_BASE64 = '';
    return '';
  }
};

export const getLogoBase64 = () => LOGO_BASE64;
export const getLogoSrc = () => LOGO_BASE64 ? `data:${LOGO_MIME};base64,${LOGO_BASE64}` : '';

// ==================== UTILIDADES ====================

export const formatearFechaReporte = (fechaISO) => {
  if (!fechaISO) return 'No disponible';
  try {
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return fechaISO; }
};

export const formatearFechaCorta = (fechaISO) => {
  if (!fechaISO) return 'No disponible';
  try {
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return fechaISO; }
};

export const formatearNumero = (numero, decimales = 2) => {
  if (numero === null || numero === undefined) return '0.00';
  return new Intl.NumberFormat('es-VE', { minimumFractionDigits: decimales, maximumFractionDigits: decimales }).format(numero);
};

export const formatearMonedaUSD = (monto) => {
  if (monto === null || monto === undefined) return '$0.00';
  return `$${formatearNumero(monto, 2)}`;
};

export const convertirEstadoTexto = (estado) => {
  if (!estado) return 'DESCONOCIDO';
  const estadoLower = estado.toString().toLowerCase();
  if (estadoLower === 'activo' || estadoLower === 'true' || estadoLower === '1') return 'ACTIVO';
  if (estadoLower === 'inactivo' || estadoLower === 'false' || estadoLower === '0') return 'INACTIVO';
  return estado.toString().toUpperCase();
};

export const getBadgeEstado = (estado) => {
  const estadoTexto = convertirEstadoTexto(estado);
  return { texto: estadoTexto, clase: estadoTexto === 'ACTIVO' ? 'estado-activo' : 'estado-inactivo' };
};

export const getStockClass = (stock, stockMinimo) => {
  const stockNum = Number(stock) || 0;
  const stockMinimoNum = Number(stockMinimo) || 0;
  if (stockNum <= 0) return 'stock-cero';
  if (stockMinimoNum > 0 && stockNum <= stockMinimoNum) return 'stock-bajo';
  return 'stock-normal';
};

export const getStockTexto = (stock, stockMinimo) => {
  const stockNum = Number(stock) || 0;
  const stockMinimoNum = Number(stockMinimo) || 0;
  if (stockNum <= 0) return 'SIN STOCK';
  if (stockMinimoNum > 0 && stockNum <= stockMinimoNum) return 'STOCK BAJO';
  return 'NORMAL';
};

export const getNombreProveedor = (producto) => {
  if (!producto) return 'Sin proveedor';
  return (
    producto.nombreProveedor || producto.nombre_proveedor || producto.NombreProveedor ||
    producto.proveedorNombre || producto.proveedor?.nombre || producto.Proveedor?.nombre ||
    (producto.proveedor && typeof producto.proveedor === 'string' ? producto.proveedor : '') ||
    'Sin proveedor'
  );
};

// ==================== CÁLCULOS ====================

export const ordenarProductosPorNombre = (productos) => {
  if (!productos || productos.length === 0) return productos;
  return [...productos].sort((a, b) => {
    const nombreA = (a.nombre || '').toLowerCase();
    const nombreB = (b.nombre || '').toLowerCase();
    return nombreA.localeCompare(nombreB, 'es');
  });
};

export const calcularEstadisticasReporte = (productos) => {
  const total = productos.length;
  const activos = productos.filter(p => p.estado?.toLowerCase() === 'activo' || p.estado === true || p.estado === 1).length;
  const stockTotal = productos.reduce((sum, p) => sum + (Number(p.stock) || 0), 0);
  const productosSinStock = productos.filter(p => !p.stock || p.stock === 0).length;
  const valorInventarioUSD = productos.reduce((sum, p) => sum + ((Number(p.precio_compra_usdt) || 0) * (Number(p.stock) || 0)), 0);
  const valorPotencialVentaUSD = productos.reduce((sum, p) => sum + ((Number(p.precio_venta_usd) || 0) * (Number(p.stock) || 0)), 0);

  return {
    total, activos, inactivos: total - activos, stockTotal, productosSinStock,
    valorInventarioUSD, valorPotencialVentaUSD, gananciaPotencial: valorPotencialVentaUSD - valorInventarioUSD
  };
};

export const filtrarProductosParaReporte = (productos, filtro) => {
  let productosFiltrados = [...productos];

  switch (filtro) {
    case 'todos': break;
    case 'activos': productosFiltrados = productosFiltrados.filter(p => p.estado?.toLowerCase() === 'activo' || p.estado === true || p.estado === 1); break;
    case 'inactivos': productosFiltrados = productosFiltrados.filter(p => p.estado?.toLowerCase() === 'inactivo' || p.estado === false || p.estado === 0); break;
    case 'stock_cero': productosFiltrados = productosFiltrados.filter(p => (Number(p.stock) || 0) === 0); break;
    case 'stock_bajo': productosFiltrados = productosFiltrados.filter(p => { const s = Number(p.stock) || 0; const m = Number(p.stock_minimo) || 0; return s > 0 && m > 0 && s <= m; }); break;
    case 'con_stock': productosFiltrados = productosFiltrados.filter(p => (p.stock || 0) > 0); break;
    default: if (!isNaN(filtro) && filtro > 0) { productosFiltrados = productosFiltrados.filter(p => p.idCategoria === filtro); } break;
  }

  return ordenarProductosPorNombre(productosFiltrados);
};

// ==================== GENERAR HTML (ESTILO PREMIUM) ====================

export const generarHTMLReporteProductos = (productos, titulo = null, filtro = 'todos') => {
  const empresa = getEmpresa();
  const fechaActual = new Date();
  const productosOrdenados = ordenarProductosPorNombre(productos);
  const estadisticas = calcularEstadisticasReporte(productosOrdenados);
  const logoSrc = getLogoSrc();

  let tituloReal = titulo;
  if (!tituloReal) {
    const titulosPorFiltro = {
      'todos': 'REPORTE GENERAL DE PRODUCTOS',
      'activos': 'PRODUCTOS ACTIVOS',
      'inactivos': 'PRODUCTOS INACTIVOS',
      'stock_cero': 'PRODUCTOS SIN STOCK',
      'stock_bajo': 'PRODUCTOS CON STOCK BAJO',
      'con_stock': 'PRODUCTOS CON STOCK DISPONIBLE'
    };
    tituloReal = titulosPorFiltro[filtro] || 'REPORTE DE PRODUCTOS';
  }

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>${tituloReal} - ${empresa.nombre}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Arial', 'Helvetica', sans-serif; background: white; padding: 0; }
    
    /* Contenedor ultra compacto */
    .reporte { max-width: 780px; margin: 0 auto; padding: 2px 10px; }

    /* ENCABEZADO: Flexbox alineado al tope */
    .header {
      display: flex;
      align-items: flex-start; 
      justify-content: space-between;
      border-bottom: 2px solid #333;
      padding-bottom: 5px;
      margin-bottom: 8px;
    }
    
    .logo-container {
      flex-shrink: 0;
      width: 110px; 
      display: flex;
      justify-content: center;
      padding-top: 4px; 
    }
    .logo-img { 
      max-height: 55px; 
      max-width: 100px; 
      object-fit: contain; 
    }
    
    .header-center {
      flex: 1;
      text-align: center;
      padding: 0 5px;
    }
    .empresa-nombre { font-size: 14px; font-weight: bold; color: #222; margin-bottom: 1px; }
    .empresa-rif { font-size: 8.5px; color: #222; font-weight: bold; margin-bottom: 1px; }
    .empresa-contactos { font-size: 8px; color: #222; line-height: 1.15; font-weight: bold; }
    .empresa-direccion {
      font-size: 8.5px; 
      color: #222; 
      line-height: 1.2; 
      margin-top: 4px; 
      font-weight: bold;
    }

    /* TÍTULO Y FECHA */
    .titulo { text-align: center; margin-bottom: 5px; }
    .titulo h1 { font-size: 11.5px; font-weight: bold; color: #1a4d8c; text-transform: uppercase; margin-bottom: 1px; }
    .fecha { font-size: 7.5px; color: #666; }

    /* STATS BAR */
    .stats-bar {
      background: #f8f9fa;
      padding: 4px 8px;
      margin-bottom: 6px;
      border: 1px solid #e0e0e0;
      font-size: 8px;
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 10px;
    }
    .stat-item { display: inline-flex; align-items: baseline; gap: 3px; }
    .stat-label { color: #555; font-weight: normal; }
    .stat-value { font-weight: bold; color: #000; }

    /* TABLA COMPACTA */
    table {
      width: 100%; border-collapse: collapse; font-size: 8px;
      border: 1px solid #ddd; table-layout: fixed;
    }
    th {
      background: #f0f0f0; padding: 3px 2px; text-align: left;
      font-weight: bold; border-bottom: 1px solid #ccc;
      color: #333; text-transform: uppercase; font-size: 7px;
    }
    td { padding: 3px 2px; border-bottom: 1px solid #eee; vertical-align: top; word-wrap: break-word; }
    tr { page-break-inside: avoid; }
    
    .text-right { text-align: right; }
    .text-center { text-align: center; }
    .font-bold { font-weight: bold; }

    /* TOTAL GENERAL */
    .total-section {
      margin-top: 6px;
      display: flex;
      justify-content: flex-end;
      page-break-inside: avoid;
    }
    .total-box {
      border-top: 1.5px solid #333;
      border-bottom: 1.5px solid #333;
      padding: 3px 6px;
      display: flex;
      gap: 10px;
      align-items: center;
      background: #f8f9fa;
    }
    .total-label { font-size: 8px; font-weight: bold; color: #333; }
    .total-amount { font-size: 10px; font-weight: bold; color: #111; }

    .footer {
      margin-top: 8px; font-size: 7px; text-align: center;
      color: #666; padding-top: 3px; border-top: 1px solid #eee;
    }

    @media print {
      @page { margin: 0.5cm; size: landscape; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <div class="reporte" id="reporte-pdf">
    
    <!-- ENCABEZADO DOBLE LOGO -->
    <div class="header">
      <div class="logo-container">
        ${logoSrc ? `<img src="${logoSrc}" class="logo-img" alt="Logo Izq">` : ''}
      </div>
      
      <div class="header-center">
        <div class="empresa-nombre">${empresa.nombre}</div>
        <div class="empresa-rif">RIF: ${empresa.rif}</div>
        <div class="empresa-contactos">
          0412-5696219 / 0416-7594723<br>
          soportejeaninformatico@gmail.com | jeaninformaticoca@gmail.com
        </div>
        <div class="empresa-direccion">
          Calle 30 con Av. 33, C.C. Latin Center P/B, Local 01 - Acarigua, Portuguesa
        </div>
      </div>
      
      <div class="logo-container">
        ${logoSrc ? `<img src="${logoSrc}" class="logo-img" alt="Logo Der">` : ''}
      </div>
    </div>

    <div class="titulo">
      <h1>${tituloReal}</h1>
      <div class="fecha">Generado el: ${formatearFechaReporte(fechaActual.toISOString())}</div>
    </div>

    <div class="stats-bar">
      <div class="stat-item"><span class="stat-label">Total:</span> <span class="stat-value">${estadisticas.total}</span></div>
      <div class="stat-item"><span class="stat-label">Activos:</span> <span class="stat-value">${estadisticas.activos}</span></div>
      <div class="stat-item"><span class="stat-label">Sin Stock:</span> <span class="stat-value">${estadisticas.productosSinStock}</span></div>
      <div class="stat-item"><span class="stat-label">Inventario:</span> <span class="stat-value">${formatearMonedaUSD(estadisticas.valorInventarioUSD)}</span></div>
    </div>

    ${productosOrdenados.length > 0 ? `
      <table>
      <colgroup>
        <col style="width: 12%;">
        <col style="width: 26%;">
        <col style="width: 16%;">
        <col style="width: 16%;">
        <col style="width: 10%;">
        <col style="width: 6%;">
        <col style="width: 7%;">
        <col style="width: 7%;">
      </colgroup>
      <thead>
        <tr>
          <th>Código</th>
          <th>Nombre</th>
          <th>Categoría</th>
          <th>Proveedor</th>
          <th>Marca</th>
          <th class="text-center">Stock</th>
          <th class="text-right">Compra</th>
          <th class="text-right">Venta</th>
        </tr>
      </thead>
      <tbody>
        ${productosOrdenados.map(p => `
          <tr>
            <td><strong>${p.codigo || '—'}</strong></td>
            <td>${p.nombre || '—'}</td>
            <td>${p.nombreCategoria || '—'}</td>
            <td>${getNombreProveedor(p)}</td>
            <td>${p.marca || '—'}</td>
            <td class="text-center font-bold">${p.stock || 0}</td>
            <td class="text-right">${formatearMonedaUSD(p.precio_compra_usdt)}</td>
            <td class="text-right font-bold" style="color: #166534;">${formatearMonedaUSD(p.precio_venta_usd)}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>

    <div class="total-section">
      <div class="total-box">
        <span class="total-label">VALOR INVENTARIO (USD):</span>
        <span class="total-amount">${formatearMonedaUSD(estadisticas.valorInventarioUSD)}</span>
      </div>
    </div>
    ` : '<div style="padding: 20px; text-align: center; border: 1px solid #ddd;">No hay productos para mostrar</div>'}

    <div class="footer">
      ${empresa.nombre} • RIF: ${empresa.rif} • Sistema de Gestión de Inventario
    </div>
  </div>
</body>
</html>`;
};

// ==================== DESCARGAR PDF (DIRECTO) ====================

export const descargarPDFProductos = async (productos, titulo = null, filtro = 'todos') => {
  if (!productos || productos.length === 0) {
    alert('No hay productos para mostrar');
    return false;
  }
  try {
    LOGO_BASE64 = '';
    LOGO_MIME = 'image/jpeg';
    await cargarLogo();

    const htmlReporte = generarHTMLReporteProductos(productos, titulo, filtro);

    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = '800px';
    container.style.background = '#fff';
    container.innerHTML = htmlReporte;
    document.body.appendChild(container);

    const elemento = container.querySelector('#reporte-pdf');
    const fecha = new Date().toISOString().split('T')[0];
    const nombreArchivo = `Reporte_Productos_${fecha}.pdf`;

    const opciones = {
      margin: [5, 5, 5, 5],
      filename: nombreArchivo,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' }, // Horizontal
      pagebreak: { mode: ['css', 'legacy'], avoid: ['tr'] } 
    };

    await html2pdf().set(opciones).from(elemento).save();
    document.body.removeChild(container);
    return true;
  } catch (error) {
    alert('Error al generar el PDF: ' + error.message);
    return false;
  }
};

// ==================== IMPRIMIR (ALTERNATIVA) ====================

export const imprimirReporteProductos = async (productos, titulo = null, filtro = 'todos') => {
  if (!productos || productos.length === 0) return false;
  try {
    await cargarLogo();
    const htmlReporte = generarHTMLReporteProductos(productos, titulo, filtro);
    const ventana = window.open('', '_blank', 'width=1100,height=700');
    if (!ventana) return false;
    ventana.document.write(htmlReporte);
    ventana.document.close();
    ventana.focus();
    return true;
  } catch (error) {
    return false;
  }
};

// ==================== EXCEL ====================

export const exportarProductosExcel = (productos, titulo = 'productos', filtro = 'todos') => {
  try {
    if (typeof window === 'undefined') return { success: false, error: 'No hay window' };
    const empresa = getEmpresa();
    const productosOrdenados = ordenarProductosPorNombre(productos);
    const XLSX = require('xlsx');
    const wb = XLSX.utils.book_new();

    const headerRows = [
      [empresa.nombre], [`RIF: ${empresa.rif}`],
      [`Calle 30 con Av. 33, C.C. Latin Center P/B, Local 01 - Acarigua`],
      [`Tel: 0412-5696219 / 0416-7594723`], [],
      ['REPORTE DE PRODUCTOS'],
      [`Generado: ${formatearFechaReporte(new Date().toISOString())}`], []
    ];

    const dataRows = productosOrdenados.map(p => [
      p.codigo || '', p.nombre || '', p.nombreCategoria || '', getNombreProveedor(p),
      p.marca || '', p.stock || 0, p.stock_minimo || 0,
      Number(p.precio_compra_usdt) || 0, Number(p.precio_venta_usd) || 0,
      (Number(p.precio_compra_usdt) || 0) * (Number(p.stock) || 0)
    ]);

    const totalInventario = dataRows.reduce((s, r) => s + (r[9] || 0), 0);
    const wsData = [...headerRows, ['Código', 'Nombre', 'Categoría', 'Proveedor', 'Marca', 'Stock', 'Stock Mín.', 'Compra ($)', 'Venta ($)', 'Valor Total'], ...dataRows, ['', '', '', '', '', '', '', '', 'TOTAL:', totalInventario]];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    XLSX.utils.book_append_sheet(wb, ws, 'Productos');
    XLSX.writeFile(wb, `${titulo}_${new Date().toISOString().split('T')[0]}.xlsx`);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ==================== PDF NATIVO (ALTERNATIVA) ====================

export const exportarProductosPDF = async (productos, title = 'Reporte de Productos', filtro = 'todos') => {
  try {
    if (typeof window === 'undefined') return { success: false, error: 'No hay window' };
    const empresa = getEmpresa();
    const productosOrdenados = ordenarProductosPorNombre(productos);
    const { jsPDF } = require('jspdf');
    require('jspdf-autotable');

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 10;
    let yPos = 8;

    // Encabezado
    doc.setDrawColor(0, 0, 0); doc.setLineWidth(0.4);
    doc.rect(margin, yPos, pageWidth - margin * 2, 22);

    let logoWidth = 0;
    const logoBase64 = getLogoBase64();
    if (logoBase64) {
      try {
        const logoHeight = 16;
        const format = empresa.logoBase64 && empresa.logoBase64.includes('image/png') ? 'PNG' : 'JPEG';
        logoWidth = logoHeight * 1.4;
        doc.addImage(`data:image/${format.toLowerCase()};base64,${logoBase64}`, format, margin + 3, yPos + 3, logoWidth, logoHeight, undefined, 'FAST');
      } catch (e) { logoWidth = 0; }
    }

    const infoX = margin + 4 + (logoWidth > 0 ? logoWidth + 3 : 0);
    doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
    doc.text(empresa.nombre, infoX, yPos + 6);
    doc.setFontSize(7.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(50, 50, 50);
    doc.text(`RIF: ${empresa.rif}`, infoX, yPos + 11);
    doc.text(`Calle 30 con Av. 33, C.C. Latin Center P/B, Local 01`, infoX, yPos + 15);
    doc.text(`Acarigua, Portuguesa | Tel: 0412-5696219 / 0416-7594723`, infoX, yPos + 19);

    doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
    doc.text(title, pageWidth - margin - 3, yPos + 8, { align: 'right' });
    doc.setFontSize(7.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60);
    doc.text(`Generado: ${formatearFechaReporte(new Date().toISOString())}`, pageWidth - margin - 3, yPos + 13, { align: 'right' });
    doc.text(`Total: ${productosOrdenados.length} productos`, pageWidth - margin - 3, yPos + 17, { align: 'right' });
    yPos += 26;

    // Stats
    const total = productosOrdenados.length;
    const conStock = productosOrdenados.filter(p => (p.stock || 0) > 0).length;
    const sinStock = total - conStock;
    const activos = productosOrdenados.filter(p => p.estado?.toLowerCase() === 'activo' || p.estado === 1 || p.estado === true).length;
    const valorInventario = productosOrdenados.reduce((sum, p) => sum + ((Number(p.precio_compra_usdt) || 0) * (Number(p.stock) || 0)), 0);

    doc.setFillColor(240, 240, 240);
    doc.rect(margin, yPos, pageWidth - margin * 2, 7, 'F');
    doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
    doc.text(`Total: ${total} | Activos: ${activos} | Sin Stock: ${sinStock} | Con Stock: ${conStock} | Inventario: $${formatearNumero(valorInventario, 2)}`, pageWidth / 2, yPos + 5, { align: 'center' });
    yPos += 10;

    // Tabla
    const tableData = productosOrdenados.map(p => [
      p.codigo || '', (p.nombre || '').substring(0, 35), p.nombreCategoria || '',
      (getNombreProveedor(p) || '').substring(0, 20), p.marca || '',
      p.stock || 0, p.stock_minimo || 0,
      formatearMonedaUSD(p.precio_compra_usdt), formatearMonedaUSD(p.precio_venta_usd),
      formatearMonedaUSD((Number(p.precio_compra_usdt) || 0) * (Number(p.stock) || 0))
    ]);

    doc.autoTable({
      startY: yPos,
      head: [['Código', 'Nombre', 'Categoría', 'Proveedor', 'Marca', 'Stock', 'Mín.', 'Compra', 'Venta', 'Valor Total']],
      body: tableData,
      foot: [['', '', '', '', '', '', '', '', 'TOTAL:', formatearMonedaUSD(valorInventario)]],
      theme: 'grid',
      headStyles: { fillColor: [0, 0, 0], textColor: 255, fontSize: 7, halign: 'center', fontStyle: 'bold' },
      bodyStyles: { fontSize: 6.5, textColor: [0, 0, 0] },
      footStyles: { fillColor: [220, 220, 220], textColor: [0, 0, 0], fontSize: 7, fontStyle: 'bold', halign: 'right' },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      styles: { cellPadding: 1.3, lineColor: [0, 0, 0], lineWidth: 0.1 },
      columnStyles: { 0: { cellWidth: 18, halign: 'center' }, 1: { cellWidth: 42 }, 2: { cellWidth: 25 }, 3: { cellWidth: 28 }, 4: { cellWidth: 20 }, 5: { cellWidth: 11, halign: 'center' }, 6: { cellWidth: 11, halign: 'center' }, 7: { cellWidth: 18, halign: 'right' }, 8: { cellWidth: 18, halign: 'right' }, 9: { cellWidth: 22, halign: 'right', fontStyle: 'bold' } },
      margin: { left: margin, right: margin },
      didDrawPage: function (data) {
        doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
        doc.text(`${empresa.nombre} • RIF: ${empresa.rif} • 0412-5696219`, margin, pageHeight - 5);
        doc.text(`Página ${data.pageNumber}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
      }
    });

    doc.save(`reporte_productos_${new Date().toISOString().split('T')[0]}.pdf`);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export default {
  JEAN_INFORMATICO_CONFIG,
  cargarLogo,
  getLogoBase64,
  getLogoSrc,
  formatearFechaReporte,
  formatearFechaCorta,
  formatearNumero,
  formatearMonedaUSD,
  convertirEstadoTexto,
  getBadgeEstado,
  getStockClass,
  getStockTexto,
  getNombreProveedor,
  ordenarProductosPorNombre,
  calcularEstadisticasReporte,
  filtrarProductosParaReporte,
  generarHTMLReporteProductos,
  descargarPDFProductos,
  imprimirReporteProductos,
  exportarProductosExcel,
  exportarProductosPDF
};